import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { CASES, CASE_FILTERS } from "../../config";
import { track } from "../../lib/api";
import { ArrowUpRight, Images } from "lucide-react";
import { CaseModal } from "./CaseModal";

const spotlight = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
};

export const Cases = ({ onBook }) => {
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const shown = filter === "all" ? CASES : CASES.filter((c) => c.type === filter);

  const openCase = (c) => {
    track("open_case", c.type, { id: c.id });
    setActive(c);
  };

  return (
    <section id="casos" data-testid="cases" className="relative z-10 py-24 sm:py-32 bg-[var(--surface)] border-y border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline mb-3">Casos destacados</p>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl leading-[1.05]">
            Problema, solución y resultado
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="case-filters">
            {CASE_FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`filter-${f.id}`}
                onClick={() => {
                  setFilter(f.id);
                  track("filter_cases", f.id);
                }}
                className={`rounded-full px-4 py-2 text-sm transition-colors duration-200 border ${
                  filter === f.id
                    ? "bg-[var(--magenta)] text-white border-transparent"
                    : "text-zinc-400 border-white/10 hover:text-white hover:border-white/25"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          <AnimatePresence mode="popLayout">
            {shown.map((c) => (
              <motion.div
                key={c.id}
                layout
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 12 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                onMouseMove={spotlight}
                data-testid={`case-card-${c.id}`}
                className="spotlight-card group rounded-3xl overflow-hidden border border-white/10 bg-black hover-lift"
              >
                <button
                  onClick={() => openCase(c)}
                  data-testid={`case-open-${c.id}`}
                  className="block w-full text-left aspect-[16/10] overflow-hidden relative"
                >
                  <img
                    src={c.image}
                    alt={c.name}
                    loading="lazy"
                    className="w-full h-full object-cover object-top opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className="rounded-full glass px-3 py-1 text-xs text-white">{c.typeLabel}</span>
                    <span className="rounded-full glass px-3 py-1 text-xs text-zinc-300">{c.sector}</span>
                  </div>
                  {c.gallery && c.gallery.length > 1 && (
                    <span className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 rounded-full glass px-3 py-1.5 text-xs text-white">
                      <Images size={13} /> {c.gallery.length} vistas
                    </span>
                  )}
                </button>
                <div className="p-6 relative z-[3]">
                  <h3 className="font-display font-semibold text-2xl">{c.name}</h3>
                  <div className="mt-4 space-y-3 text-sm">
                    <p><span className="text-[var(--magenta)] font-medium">Problema · </span><span className="text-zinc-400">{c.problem}</span></p>
                    <p><span className="text-emerald-400 font-medium">Solución · </span><span className="text-zinc-300">{c.solution}</span></p>
                  </div>
                  <div className="mt-6 flex items-center gap-5">
                    <button
                      onClick={() => openCase(c)}
                      data-testid={`case-view-${c.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:text-[var(--magenta)] transition-colors duration-200"
                    >
                      Ver proyecto <Images size={15} />
                    </button>
                    <button
                      onClick={onBook}
                      data-testid={`case-cta-${c.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-400 hover:text-white transition-colors duration-200"
                    >
                      Quiero algo así <ArrowUpRight size={15} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
      <CaseModal caseItem={active} onOpenChange={setActive} onBook={onBook} />
    </section>
  );
};
